import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';

const LoginForm = ({ onSubmit }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  const { login } = useAuth(); // Move useAuth hook to the top level
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Error state
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    form: ''
  });
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({
      email: '',
      password: '',
      form: ''
    });
    
    // Validate inputs
    let isValid = true;
    
    if (!email) {
      setErrors(prev => ({ ...prev, email: 'Email is required' }));
      isValid = false;
    } else if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      isValid = false;
    }
    
    if (!password) {
      setErrors(prev => ({ ...prev, password: 'Password is required' }));
      isValid = false;
    } else if (password.length < 6) {
      setErrors(prev => ({ ...prev, password: 'Password must be at least 6 characters' }));
      isValid = false;
    }
    
    if (!isValid) return;
    
    try {
      setIsLoading(true);
      
      // Call Firebase authentication
      const result = await login(email, password, rememberMe);
      
      if (result.success) {
        // Redirect to the dashboard or the page they were trying to access
        navigate(from, { replace: true });
        console.log('Login successful!', { email, rememberMe });
      } else {
        // Handle authentication error
        setErrors(prev => ({ 
          ...prev, 
          form: result.error || 'An error occurred during login. Please try again.' 
        }));
      }
      
      // Call the onSubmit prop with form data if provided
      if (onSubmit) {
        await onSubmit({ email, password, rememberMe, success: result.success });
      }
      
    } catch (error) {
      // Handle login error
      setErrors(prev => ({ 
        ...prev, 
        form: error.message || 'An error occurred during login. Please try again.' 
      }));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check for remembered email on component mount
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);
  
  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg={bgColor}
      p={8}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      boxShadow="lg"
      width="100%"
      maxW="400px"
    >
      <VStack spacing={4} align="flex-start" width="100%">
        <Text fontSize="2xl" fontWeight="bold">Login</Text>
        
        {errors.form && (
          <Box 
            p={3} 
            bg="red.100" 
            color="red.800" 
            width="100%" 
            borderRadius="md"
          >
            {errors.form}
          </Box>
        )}
        
        <FormControl isInvalid={!!errors.email}>
          <FormLabel htmlFor="email">Email</FormLabel>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
          />
          <FormErrorMessage>{errors.email}</FormErrorMessage>
        </FormControl>
        
        <FormControl isInvalid={!!errors.password}>
          <FormLabel htmlFor="password">Password</FormLabel>
          <InputGroup>
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
            <InputRightElement width="4.5rem">
              <Button
                h="1.75rem"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </Button>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{errors.password}</FormErrorMessage>
        </FormControl>
        
        <Stack 
          direction={{ base: 'column', sm: 'row' }} 
          justify="space-between" 
          width="100%"
          align={{ base: 'flex-start', sm: 'center' }}
        >
          <Checkbox
            isChecked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            colorScheme="blue"
          >
            Remember me
          </Checkbox>
          
          <Button 
            variant="link" 
            colorScheme="blue" 
            size="sm"
            as="a"
            href="/forgot-password"
          >
            Forgot password?
          </Button>
        </Stack>
        
        <Button
          type="submit"
          colorScheme="teal"
          width="100%"
          mt={4}
          isLoading={isLoading}
          loadingText="Logging in"
        >
          Sign in
        </Button>
        
        <Box textAlign="center" mt={4}>
          <Text fontSize="sm">
            Don't have an account?{' '}
            <Button variant="link" colorScheme="blue" size="sm" as="a" href="/register">
              Create one now
            </Button>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default LoginForm;
